import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { CheckIcon, ClockCounterClockwiseIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { cn } from 'app/lib/utils'

const DEFAULT_BAND_ICON = <ClockCounterClockwiseIcon size={16} />

export type BandOption<T> = {
  id: T
  label: string
  /** What picking this one changes about the call. Shown under the label. */
  blurb?: string
}

/**
 * Which window of a queue is on screen.
 *
 * Every one of these boards slices its population by time, four or five ways,
 * and each slice needs a sentence to say what it changes about the call: a
 * fortnight-old case is being reminded, a year-old one is being re-presented.
 * As a row of pills that reasoning had nowhere to go, and the pills were the
 * widest control in the header, wrapping the row on an ordinary window. A menu
 * holds both the choice and the reason, in the width of one button.
 *
 * `noun` is the word the windows are measured from, printed on the trigger
 * beside whichever label is chosen. Treatment needs it: "0 to 30 days" beside
 * a locations filter does not say a length of time since what, and a reader
 * left to guess lands on the patient's age rather than the plan's. Hygiene
 * does not: its windows name themselves ("Due within 30 days"), and a prefix
 * there would only be a word that says nothing.
 */
export function BandPicker<T>({
  noun,
  menuLabel,
  options,
  value,
  onSelect,
  icon = DEFAULT_BAND_ICON,
  triggerClassName,
}: {
  noun?: string
  /** Heading over the list, naming what the windows measure. */
  menuLabel: string
  options: BandOption<T>[]
  value: T
  onSelect: (id: T) => void
  /**
   * The glyph on the trigger. The clock by default, because every picker
   * measured time until the treatment board's "Booked" cut, which measures
   * the diary and wants a calendar so two menus side by side do not look
   * like one control drawn twice.
   */
  icon?: React.ReactElement
  triggerClassName?: string
}) {
  const current = options.find((option) => option.id === value) ?? options[0]
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {/* size="sm" is h-8, the height the search box and the segmented
            controls beside it share. */}
        <Button
          variant="white"
          size="sm"
          className={cn(`shrink-0 @max-xl/toolbar:px-2`, triggerClassName)}
          title={[noun, current?.label].filter(Boolean).join(` `)}
        >
          {icon}
          {noun && <span className="text-ui-xs text-subtle @max-3xl/toolbar:hidden">{noun}</span>}
          <span className="text-ui-xs @max-xl/toolbar:hidden">{current?.label}</span>
          <ChevronDownIcon className="h-4 w-4 text-minimal @max-xl/toolbar:hidden" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 bg-surface-raised">
        <DropdownMenuLabel className="text-label-xs uppercase tracking-wide text-subtle">{menuLabel}</DropdownMenuLabel>
        {options.map((option, index) => {
          const selected = option.id === current?.id
          return (
            <DropdownMenuItem
              // An option's id is legitimately undefined (the whole cohort is
              // the absence of a window), so the index backs the key up.
              key={String(option.id ?? index)}
              onSelect={() => !selected && onSelect(option.id)}
              className="flex items-start gap-2 py-2"
            >
              <CheckIcon
                size={14}
                weight="bold"
                // Held in the layout rather than removed, so the labels stay
                // on one left edge whichever row is ticked.
                className={cn(`mt-0.5 shrink-0 text-strong`, !selected && `invisible`)}
              />
              <span className="min-w-0">
                <span className={cn(`block text-body-xs`, selected ? `font-semibold text-strong` : `text-default`)}>
                  {option.label}
                </span>
                {option.blurb && (
                  <span className="mt-0.5 block whitespace-normal text-body-xs leading-relaxed text-subtle">
                    {option.blurb}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
